import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getAllIndustries, getIndustryById } from '../data/industries';
import CTA from '../components/CTA';
import OurCommitment from '../components/OurCommitment';
import { generateCTAMessage } from '../utils/ctaMessages.js';

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 2000, delay = 0 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const el = ref.current;
    if (el) {
      observer.observe(el);
    }

    return () => {
      if (el) {
        observer.unobserve(el);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const timer = setTimeout(() => {
      const numericValue = parseFloat(value.replace(/[^\d.-]/g, ''));
      const isPercentage = value.includes('%');
      const isRange = value.includes('-');
      
      if (isRange) {
        // Handle ranges like "40-60%"
        const [start, end] = value.split('-').map(v => parseFloat(v.replace(/[^\d.-]/g, '')));
        const step = (end - start) / (duration / 16);
        let current = start;
        
        const interval = setInterval(() => {
          current += step;
          if (current >= end) {
            current = end;
            clearInterval(interval);
          }
          setCount(current);
        }, 16);
        
        return () => clearInterval(interval);
      } else if (isPercentage) {
        // Handle percentages
        const step = numericValue / (duration / 16);
        let current = 0;
        
        const interval = setInterval(() => {
          current += step;
          if (current >= numericValue) {
            current = numericValue;
            clearInterval(interval);
          }
          setCount(current);
        }, 16);
        
        return () => clearInterval(interval);
      } else if (value.includes('/')) {
        // Handle values like "24/7"
        setCount(value);
        return;
      } else {
        // Handle regular numbers
        const step = numericValue / (duration / 16);
        let current = 0;
        
        const interval = setInterval(() => {
          current += step;
          if (current >= numericValue) {
            current = numericValue;
            clearInterval(interval);
          }
          setCount(current);
        }, 16);
        
        return () => clearInterval(interval);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [isVisible, value, duration, delay]);

  const formatValue = () => {
    if (value.includes('/')) return value; // "24/7"
    if (value.includes('-')) return value; // "40-60%"
    
    const isPercentage = value.includes('%');
    const isDecimal = count % 1 !== 0;
    
    if (isPercentage) {
      return `${Math.round(count)}%`;
    } else if (isDecimal) {
      return count.toFixed(1);
    } else {
      return Math.round(count);
    }
  };

  return (
    <span ref={ref}>
      {formatValue()}
    </span>
  );
};

// Import project data for integration
const projectData = [
  {
    id: "car-lease-chatbot",
    title: "SeezAuto - AI Sales Assistant for Car Leasing",
    industry: "transport",
    tech_stack: ["FastAPI", "Docker", "AWS EC2", "LangChain", "RAG", "LLMs"],
    impact: [
      { metric: "15%", label: "Sales increase" },
      { metric: "40%", label: "Faster lead qualification" },
      { metric: "90%", label: "Customer satisfaction" }
    ]
  },
  {
    id: "ai-route-optimization",
    title: "AI Route Optimization System",
    industry: "transport",
    tech_stack: ["Route Optimization", "Predictive Analytics", "Fleet Management", "IoT Sensors"],
    impact: [
      { metric: "35%", label: "Efficiency improvement" },
      { metric: "30%", label: "Cost reduction" },
      { metric: "85%", label: "On-time delivery" }
    ]
  },
  {
    id: "teacher-rag-chatbot",
    title: "SullyEdu - Smart Teacher Assistant Platform",
    industry: "education",
    tech_stack: ["LangChain", "LangGraph", "Agentic AI", "Email Automation"],
    impact: [
      { metric: "5h/d", label: "Time saved per teacher" },
      { metric: "95%", label: "Assignment accuracy" },
      { metric: "80%", label: "Student engagement" }
    ]
  },
  {
    id: "genai-interview-assistant",
    title: "InterviewPro - AI-Powered Career Coach",
    industry: "education",
    tech_stack: ["GPT-4o", "OpenAI Whisper", "RAG", "Electron/desktop", "Tailwind CSS", "Docker"],
    impact: [
      { metric: "70%", label: "Increase in user engagement" },
      { metric: "60%", label: "Faster resume tailoring" },
      { metric: "85%", label: "Interview success rate" }
    ]
  },
  {
    id: "bert-sms-classification",
    title: "SMSGuard - Mobile Security Protection Suite",
    industry: "cybersecurity",
    tech_stack: ["BERT", "Python", "NLP"],
    impact: [
      { metric: "98%", label: "Classification accuracy" },
      { metric: "40%", label: "Decrease in cyber attacks" },
      { metric: "99.9%", label: "Uptime reliability" }
    ]
  },
  {
    id: "recommendation-engine-retail",
    title: "ShopSmart AI - Personalized Shopping Engine",
    industry: "retail",
    tech_stack: ["Collaborative Filtering", "Random Forest", "GBM", "Pandas", "NumPy", "AWS Redshift"],
    impact: [
      { metric: "$1M", label: "Revenue influenced" },
      { metric: "40%", label: "Faster data access" },
      { metric: "75%", label: "Conversion rate" }
    ]
  },
  {
    id: "movie-agent",
    title: "CineMatch AI - Smart Entertainment Discovery",
    industry: "retail",
    tech_stack: ["LangChain", "LLMs", "TMDB API", "Agents"],
    impact: [
      { metric: "90%+", label: "Success in understanding queries" },
      { metric: "98%", label: "Higher user satisfaction" },
      { metric: "85%", label: "Content discovery rate" }
    ]
  },
  {
    id: "email-classification-cyber",
    title: "SecureMail Pro - Advanced Email Security Suite",
    industry: "cybersecurity",
    tech_stack: ["TF-IDF", "Word2Vec", "Random Forest", "Anomaly Detection"],
    impact: [
      { metric: "95%", label: "Classification accuracy" },
      { metric: "40%", label: "Reduction in malicious emails" },
      { metric: "99.5%", label: "False positive rate" }
    ]
  },
  {
    id: "id-card-ocr-kyc",
    title: "MandolinKYC - Smart Document Verification System",
    industry: "banking",
    tech_stack: ["easyocr", "Python", "OCR", "Image Preprocessing"],
    impact: [
      { metric: "40%", label: "Faster identity verification" },
      { metric: "98%", label: "OCR accuracy rate" },
      { metric: "90%", label: "User satisfaction" }
    ]
  },
  {
    id: "pdf-rag-chatbot-milvus",
    title: "DocuMind AI - Intelligent Document Assistant",
    industry: "banking",
    tech_stack: ["Streamlit", "Milvus", "OpenAI API", "RAG"],
    impact: [
      { metric: "40%", label: "Improvement in retrieval accuracy" },
      { metric: "100%", label: "Trustworthy answers with sources" },
      { metric: "85%", label: "Query resolution rate" }
    ]
  },
  {
    id: "healthcare-appointment-assistant",
    title: "MediConnect Pro - Smart Healthcare Assistant",
    industry: "healthcare",
    tech_stack: ["OpenAI GPT", "FastMCP", "Python", "Voice Chat API", "Email Automation", "Multi-language Support"],
    impact: [
      { metric: "85%", label: "Automated bookings" },
      { metric: "70%", label: "Faster scheduling" },
      { metric: "90%", label: "Patient satisfaction" }
    ]
  },
  {
    id: "healthcare-transport-automation",
    title: "AxleFlow - Intelligent Transport Dispatch System",
    industry: "healthcare",
    tech_stack: ["Python", "FastAPI", "Docker", "AWS", "NLP", "RAG"],
    impact: [
      { metric: "80%", label: "Automated requests" },
      { metric: "60%", label: "Faster response time" },
      { metric: "95%", label: "Patient satisfaction" }
    ]
  }
];

const IndustriesPage = () => {
  const { industryId } = useParams();
  const navigate = useNavigate();
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // const [activeTab, setActiveTab] = useState('overview');
  const [scrollProgress, setScrollProgress] = useState(0);

  // Get industry-specific projects
  const getIndustryProjects = (industryId) => {
    return projectData.filter(project => project.industry === industryId);
  };

  // Black and sky blue color scheme
  const industryBranding = {
    healthcare: {
      primaryColor: '#000000',
      secondaryColor: '#0ea5e9',
      gradient: '#0ea5e9',
      icon: 'bi-heart-pulse'
    },
    banking: {
      primaryColor: '#000000',
      secondaryColor: '#0ea5e9',
      gradient: '#0ea5e9',
      icon: 'bi-bank'
    },
    retail: {
      primaryColor: '#000000',
      secondaryColor: '#0ea5e9',
      gradient: '#0ea5e9',
      icon: 'bi-shop'
    },
    transport: {
      primaryColor: '#000000',
      secondaryColor: '#0ea5e9',
      gradient: '#0ea5e9',
      icon: 'bi-truck'
    },
    cybersecurity: {
      primaryColor: '#000000',
      secondaryColor: '#0ea5e9',
      gradient: '#0ea5e9',
      icon: 'bi-shield-check'
    },
    education: {
      primaryColor: '#000000',
      secondaryColor: '#0ea5e9',
      gradient: '#0ea5e9',
      icon: 'bi-mortarboard'
    }
  };

  useEffect(() => {
    setIsLoading(true);
    if (industryId) {
      console.log('Loading industry with ID:', industryId);
      const industry = getIndustryById(industryId);
      console.log('Found industry:', industry);
      setSelectedIndustry(industry);
    }
    setIsLoading(false);
  }, [industryId]);

  // Scroll progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setScrollProgress(scrollPercent);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.04) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>
        
        {/* Floating Particles */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          pointerEvents: 'none'
        }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              background: 'rgba(56, 189, 248, 0.4)',
              borderRadius: '50%',
              top: `${15 + i * 12}%`,
              left: `${8 + i * 15}%`,
              animation: `float ${4 + i * 0.3}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`
            }}></div>
          ))}
        </div>
        
        <div style={{
          color: 'white',
          fontSize: 'clamp(1rem, 2.2vw, 1.2rem)',
          textAlign: 'center',
          position: 'relative',
          zIndex: 3
        }}>
          <div style={{ marginBottom: 'clamp(0.75rem, 2vw, 1rem)' }}>Loading industry information...</div>
          <div style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>🏥</div>
        </div>
      </div>
    );
  }

  // Handle invalid industry ID
  if (!selectedIndustry) {
  return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.04) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>
        
        {/* Floating Particles */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          pointerEvents: 'none'
        }}>
          {[...Array(8)].map((_, i) => (
            <div key={i} style={{
              position: 'absolute',
              width: '6px',
              height: '6px',
              background: 'rgba(56, 189, 248, 0.4)',
              borderRadius: '50%',
              top: `${15 + i * 12}%`,
              left: `${8 + i * 15}%`,
              animation: `float ${4 + i * 0.3}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`
            }}></div>
          ))}
        </div>
        
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: 'clamp(12px, 3vw, 24px)',
          padding: 'clamp(1.5rem, 4vw, 3rem)',
          textAlign: 'center',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(14, 165, 233, 0.2)',
          maxWidth: 'clamp(300px, 80vw, 600px)',
          margin: 'clamp(1rem, 3vw, 2rem)',
          position: 'relative',
          zIndex: 3
        }}>
          <h1 style={{
            color: 'white',
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            marginBottom: 'clamp(0.75rem, 2vw, 1rem)'
          }}>
            Industry Not Found
          </h1>
          <p style={{
            color: 'rgba(255, 255, 255, 0.8)',
            fontSize: 'clamp(1rem, 2.2vw, 1.1rem)',
            marginBottom: 'clamp(1.5rem, 3vw, 2rem)'
          }}>
            The industry "{industryId}" could not be found. Please select from the available industries below.
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(150px, 30vw, 200px), 1fr))',
            gap: 'clamp(0.75rem, 2vw, 1rem)',
            marginBottom: 'clamp(1.5rem, 3vw, 2rem)'
          }}>
            {getAllIndustries().map((industry) => (
              <Link
                key={industry.id}
                to={`/industries/${industry.id}`}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: 'clamp(8px, 2vw, 12px)',
                  padding: 'clamp(0.75rem, 2vw, 1rem)',
                  textDecoration: 'none',
                  color: 'white',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'clamp(0.5rem, 1.5vw, 0.75rem)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span style={{ fontSize: 'clamp(1.2rem, 3vw, 1.5rem)' }}>{industry.icon}</span>
                <span style={{ fontWeight: '600' }}>{industry.title.split(' ')[0]}</span>
              </Link>
            ))}
          </div>
          
          <Link
            to="/"
            style={{
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
              border: 'none',
              color: '#ffffff',
              padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1.5rem, 4vw, 2rem)',
              borderRadius: 'clamp(8px, 2vw, 12px)',
              fontWeight: '600',
              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'clamp(0.375rem, 1vw, 0.5rem)',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(14, 165, 233, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(14, 165, 233, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(14, 165, 233, 0.3)';
            }}
          >
            <i className="bi bi-house"></i>
            <span>Back to Home</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: `${scrollProgress}%`,
        height: '3px',
        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
        zIndex: 9999,
        transition: 'width 0.1s ease-out'
      }}></div>

      {/* Breadcrumb Navigation - Mobile Responsive */}
      <div style={{
        position: 'fixed',
        top: 'clamp(60px, 10vw, 65px)',
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(14, 165, 233, 0.1)',
        padding: 'clamp(0.75rem, 2vw, 1rem) 0',
        zIndex: 999,
        transition: 'all 0.3s ease'
      }}>
        <div className="container">
          <nav style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Link to="/" style={{
              color: '#64748b',
              textDecoration: 'none',
              fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(0.2rem, 0.5vw, 0.25rem)',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#0ea5e9'}
            onMouseLeave={(e) => e.target.style.color = '#64748b'}>
              <i className="bi bi-house"></i>
              <span>Home</span>
                </Link>
            <i className="bi bi-chevron-right" style={{ color: '#cbd5e1', fontSize: 'clamp(0.7rem, 1.6vw, 0.8rem)' }}></i>
            <Link to="/industries" style={{
              color: '#64748b',
              textDecoration: 'none',
              fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#0ea5e9'}
            onMouseLeave={(e) => e.target.style.color = '#64748b'}>
              Industries
            </Link>
            <i className="bi bi-chevron-right" style={{ color: '#cbd5e1', fontSize: 'clamp(0.7rem, 1.6vw, 0.8rem)' }}></i>
            <span style={{
              color: '#0ea5e9',
              fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
              fontWeight: '600'
            }}>
              {selectedIndustry?.title}
            </span>
          </nav>
              </div>
            </div>


      {/* Hero Section - Mobile Responsive */}
      <section id="hero" className="industry-hero-section" style={{
        paddingTop: 'clamp(100px, 15vw, 130px)' // Account for fixed header + breadcrumb height
      }}>
        <div className="hero-background" style={{ 
          background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
          position: 'relative',
          overflow: 'hidden',
          minHeight: 'clamp(70vh, 80vh, 90vh)',
          display: 'flex',
          alignItems: 'center',
          paddingBottom: 'clamp(2rem, 6vw, 4rem)'
        }}>
          {/* Background Pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.06) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.04) 0%, transparent 50%)
            `,
            zIndex: 1
          }}></div>
          
          {/* Floating Particles */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            pointerEvents: 'none'
          }}>
            {[...Array(8)].map((_, i) => (
              <div key={i} style={{
                position: 'absolute',
                width: '6px',
                height: '6px',
                background: 'rgba(56, 189, 248, 0.4)',
                borderRadius: '50%',
                top: `${15 + i * 12}%`,
                left: `${8 + i * 15}%`,
                animation: `float ${4 + i * 0.3}s ease-in-out infinite`,
                animationDelay: `${i * 0.4}s`
              }}></div>
            ))}
              </div>

          <div className="container" style={{ position: 'relative', zIndex: 3 }}>
            <div className="row justify-content-center text-center">
              <div className="col-lg-10">
                <div className="hero-content">
                  {/* Industry Icon */}
                  <div className="industry-icon-large mb-4" style={{
                    display: 'inline-block',
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '2px solid rgba(14, 165, 233, 0.3)',
                    borderRadius: '50%',
                    padding: 'clamp(1rem, 3vw, 2rem)',
                    backdropFilter: 'blur(15px)',
                    boxShadow: '0 8px 25px rgba(14, 165, 233, 0.2)'
                  }}>
                    <span style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)' }}>{selectedIndustry.icon}</span>
                  </div>
                  
                  {/* Title and Subtitle */}
                  <h1 className="hero-title mb-3" style={{
                    fontSize: 'clamp(2rem, 4vw, 3rem)',
                    fontWeight: '800',
                    color: '#ffffff',
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                    lineHeight: '1.2',
                    marginBottom: '2rem'
                  }}>{selectedIndustry.title}</h1>
                  
                  <h2 className="hero-subtitle mb-4" style={{
                    fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: '400',
                    lineHeight: '1.4',
                    marginBottom: '2rem'
                  }}>{selectedIndustry.subtitle}</h2>
                  
                  {/* Overview */}
                  <p className="hero-description mb-5" style={{
                    fontSize: 'clamp(1rem, 2.2vw, 1.1rem)',
                    color: 'rgba(255, 255, 255, 0.8)',
                    maxWidth: 'clamp(300px, 80vw, 800px)',
                    margin: '0 auto 3rem',
                    lineHeight: '1.6'
                  }}>{selectedIndustry.overview}</p>
                  
                  {/* CTA Buttons */}
                  <div className="hero-cta industries-hero-cta" style={{
                    display: 'flex',
                    gap: 'clamp(1rem, 3vw, 1.5rem)',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    marginBottom: '2rem'
                  }}>
                    <Link to="/contact" style={{
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                      border: 'none',
                      color: '#ffffff',
                      padding: 'clamp(1rem, 3vw, 1.4rem) clamp(2rem, 6vw, 3.5rem)',
                      borderRadius: 'clamp(40px, 8vw, 60px)',
                      fontWeight: '700',
                      fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                      minWidth: 'clamp(180px, 40vw, 220px)',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 12px 35px rgba(14, 165, 233, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 8px 25px rgba(14, 165, 233, 0.3)';
                    }}>
                      <i className="bi bi-rocket-takeoff"></i>
                      Start Your Project
                    </Link>
                    
                    <Link to="/case-studies" style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      color: '#ffffff',
                      padding: 'clamp(1rem, 3vw, 1.4rem) clamp(2rem, 6vw, 3.5rem)',
                      borderRadius: 'clamp(40px, 8vw, 60px)',
                      fontWeight: '700',
                      fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                      transition: 'all 0.3s ease',
                      backdropFilter: 'blur(15px)',
                      minWidth: 'clamp(180px, 40vw, 220px)',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    }}>
                      <i className="bi bi-eye"></i>
                      View Case Studies
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Stats Section - Mobile Responsive */}
      <section id="stats" className="stats-section py-5" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        padding: 'clamp(3rem, 8vw, 6rem) 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(14, 165, 233, 0.06) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          {/* Section Header */}
          <div className="text-center mb-5">
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: '900',
              color: '#1e293b',
              marginBottom: '1rem',
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Proven Results
            </h2>
            <p style={{
              fontSize: 'clamp(1rem, 2.2vw, 1.2rem)',
              color: '#64748b',
              maxWidth: 'clamp(300px, 80vw, 600px)',
              margin: '0 auto',
              lineHeight: '1.6'
            }}>
              Real impact delivered to {selectedIndustry.title.toLowerCase()} organizations
            </p>
                </div>

          <div className="row g-3 g-md-4">
            {selectedIndustry.stats.map((stat, index) => {
              // Healthcare-specific icons
              const getStatIcon = (label) => {
                if (label.toLowerCase().includes('documentation')) return 'bi-file-earmark-text';
                if (label.toLowerCase().includes('self-service') || label.toLowerCase().includes('queries')) return 'bi-chat-dots';
                if (label.toLowerCase().includes('accuracy') || label.toLowerCase().includes('coding')) return 'bi-check-circle';
                if (label.toLowerCase().includes('support') || label.toLowerCase().includes('availability')) return 'bi-headset';
                return 'bi-graph-up-arrow';
              };

              // Healthcare-specific context
              const getStatContext = (label) => {
                if (label.toLowerCase().includes('documentation')) return 'Clinicians spend less time on paperwork and more time with patients';
                if (label.toLowerCase().includes('self-service')) return 'Patients get instant answers to common questions';
                if (label.toLowerCase().includes('accuracy')) return 'Reduced billing errors and improved revenue cycle';
                if (label.toLowerCase().includes('support')) return 'Round-the-clock assistance for better patient care';
                return 'Measurable improvement in operational efficiency';
              };

              return (
                <div key={index} className="col-12 col-sm-6 col-lg-3">
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 'clamp(20px, 4vw, 28px)',
                    padding: 'clamp(1.5rem, 4vw, 3rem) clamp(1rem, 3vw, 2rem)',
                    textAlign: 'center',
                    border: '1px solid rgba(14, 165, 233, 0.15)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: 'clamp(250px, 40vw, 300px)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 30px 60px rgba(14, 165, 233, 0.2)';
                    e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.08)';
                    e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.15)';
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                  }}>
                    {/* Background Pattern */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `
                        radial-gradient(circle at 30% 20%, rgba(14, 165, 233, 0.05) 0%, transparent 50%),
                        radial-gradient(circle at 70% 80%, rgba(14, 165, 233, 0.03) 0%, transparent 50%)
                      `,
                      zIndex: 1
                    }}></div>

                    <div style={{ position: 'relative', zIndex: 2 }}>
                      {/* Icon */}
                      <div style={{
                        width: 'clamp(50px, 10vw, 70px)',
                        height: 'clamp(50px, 10vw, 70px)',
                        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto clamp(1rem, 2.5vw, 1.5rem)',
                        boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                        transition: 'all 0.3s ease'
                      }}>
                        <i className={`bi ${getStatIcon(stat.label)}`} style={{
                          fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
                          color: 'white'
                        }}></i>
              </div>

                      {/* Animated Number */}
                      <div style={{
                        fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
                        fontWeight: '900',
                        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        marginBottom: 'clamp(0.75rem, 2vw, 1rem)',
                        lineHeight: '1',
                        minHeight: 'clamp(3rem, 6vw, 4rem)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <AnimatedCounter 
                          value={stat.number} 
                          duration={2000} 
                          delay={index * 200}
                        />
            </div>

                      {/* Label */}
                      <div style={{
                        fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                        color: '#1e293b',
                        fontWeight: '700',
                        marginBottom: 'clamp(0.5rem, 1.5vw, 0.75rem)',
                        lineHeight: '1.3'
                      }}>{stat.label}</div>

                      {/* Context (hidden by default, shown on hover) */}
                      <div style={{
                        fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
                        color: '#64748b',
                        lineHeight: '1.5',
                        opacity: 0,
                        transform: 'translateY(10px)',
                        transition: 'all 0.3s ease',
                        marginTop: 'auto'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.opacity = '1';
                        e.target.style.transform = 'translateY(0)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = '0';
                        e.target.style.transform = 'translateY(10px)';
                      }}>
                        {getStatContext(stat.label)}
                </div>
              </div>
            </div>
                </div>
              );
            })}
              </div>
            </div>
      </section>



      {/* Challenges Section - Mobile Responsive */}
      <section id="challenges" className="challenges-section py-5" style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: 'clamp(3rem, 8vw, 6rem) 0'
      }}>
        {/* Enhanced Background Effects */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 15% 85%, rgba(14, 165, 233, 0.12) 0%, transparent 60%),
            radial-gradient(circle at 85% 15%, rgba(14, 165, 233, 0.08) 0%, transparent 60%),
            radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 0%, transparent 60%),
            radial-gradient(circle at 25% 25%, rgba(14, 165, 233, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(16, 185, 129, 0.03) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        {/* Subtle Grid Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(14, 165, 233, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          zIndex: 1
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '900',
                color: '#ffffff',
                textShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                lineHeight: '1.1',
                marginBottom: '2rem'
              }}>
                Key Challenges We Solve
              </h2>
              <p style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
                color: 'rgba(255, 255, 255, 0.95)',
                lineHeight: '1.7',
                maxWidth: 'clamp(300px, 90vw, 900px)',
                margin: '0 auto 3rem',
                fontWeight: '500'
              }}>
                Understanding the unique challenges in {selectedIndustry.title.toLowerCase()} and how AI can address them
              </p>
                </div>
              </div>

          <div className="row g-3 g-md-4">
            {selectedIndustry.challenges.map((challenge, index) => (
              <div key={index} className="col-12 col-lg-6">
                <div style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(14, 165, 233, 0.2)',
                  borderRadius: 'clamp(16px, 3vw, 24px)',
                  padding: 'clamp(1.5rem, 4vw, 3rem)',
                  height: '100%',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  minHeight: 'clamp(300px, 50vw, 350px)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                }}>
                  <div className="challenge-icon" style={{
                    width: 'clamp(60px, 12vw, 80px)',
                    height: 'clamp(60px, 12vw, 80px)',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto clamp(1rem, 3vw, 2rem)',
                    fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                    color: 'white',
                    boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                    transition: 'all 0.3s ease'
                  }}>
                    <i className={`bi ${challenge.icon}`}></i>
            </div>
                  <h4 style={{ 
                    color: 'white', 
                    marginBottom: 'clamp(1rem, 2.5vw, 1.5rem)',
                    fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
                    fontWeight: '700'
                  }}>{challenge.title}</h4>
                  <p style={{ 
                    color: 'rgba(255, 255, 255, 0.9)', 
                    margin: 0, 
                    lineHeight: '1.7',
                    fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                  }}>
                    {challenge.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section - Mobile Responsive */}
      <section id="solutions" className="solutions-section py-5" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: 'clamp(3rem, 8vw, 6rem) 0'
      }}>
        {/* Enhanced Background Effects */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(14, 165, 233, 0.06) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '900',
                color: '#1e293b',
                lineHeight: '1.1',
                marginBottom: '2rem'
              }}>
                Our AI Solutions
              </h2>
              <p style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
                color: '#64748b',
                lineHeight: '1.7',
                maxWidth: 'clamp(300px, 90vw, 900px)',
                margin: '0 auto 3rem',
                fontWeight: '500'
              }}>
                Comprehensive AI solutions designed specifically for {selectedIndustry.title.toLowerCase()} challenges
              </p>
                  </div>
                </div>

          <div className="row g-3 g-md-4">
            {selectedIndustry.solutions.map((solution, index) => (
              <div key={index} className="col-12 col-lg-6">
                <div style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(14, 165, 233, 0.2)',
                  borderRadius: 'clamp(12px, 3vw, 24px)',
                  padding: 'clamp(1.5rem, 4vw, 3rem)',
                  textAlign: 'center',
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.06)';
                  e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                }}>
                  <div className="solution-icon" style={{
                    width: 'clamp(60px, 12vw, 80px)',
                    height: 'clamp(60px, 12vw, 80px)',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto clamp(1rem, 3vw, 2rem)',
                    fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                    color: 'white',
                    boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                    transition: 'all 0.3s ease'
                  }}>
                    <i className={`bi ${solution.icon}`}></i>
                  </div>
                  <h4 style={{ 
                    color: '#1e293b', 
                    marginBottom: 'clamp(1rem, 2.5vw, 1.5rem)',
                    fontSize: 'clamp(1.1rem, 3vw, 1.4rem)',
                    fontWeight: '700',
                    lineHeight: '1.3'
                  }}>{solution.service}</h4>
                  <p style={{ 
                    color: '#64748b', 
                    marginBottom: 'clamp(1rem, 3vw, 2rem)',
                    lineHeight: '1.7',
                    fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                  }}>
                    {solution.description}
                  </p>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'clamp(0.375rem, 1vw, 0.5rem)',
                    justifyContent: 'center'
                  }}>
                    {solution.features.map((feature, featureIndex) => (
                      <span key={featureIndex} style={{
                        background: 'rgba(14, 165, 233, 0.1)',
                        border: '1px solid rgba(14, 165, 233, 0.2)',
                        borderRadius: 'clamp(15px, 3vw, 20px)',
                        padding: 'clamp(0.375rem, 1vw, 0.5rem) clamp(0.75rem, 2vw, 1rem)',
                        fontSize: 'clamp(0.75rem, 1.8vw, 0.9rem)',
                        fontWeight: '600',
                        color: '#0ea5e9',
                        lineHeight: '1.3'
                      }}>
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations & Compliance Section */}
      <section id="integrations" className="integrations-section py-5" style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: 'clamp(3rem, 8vw, 6rem) 0'
      }}>
        {/* Enhanced Background Effects */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 15% 85%, rgba(14, 165, 233, 0.12) 0%, transparent 60%),
            radial-gradient(circle at 85% 15%, rgba(14, 165, 233, 0.08) 0%, transparent 60%),
            radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 0%, transparent 60%),
            radial-gradient(circle at 25% 25%, rgba(14, 165, 233, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(16, 185, 129, 0.03) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        {/* Subtle Grid Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(14, 165, 233, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(14, 165, 233, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          zIndex: 1
        }}></div>
        
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '900',
                color: '#ffffff',
                textShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                lineHeight: '1.1',
                marginBottom: '2rem'
              }}>
                Seamless Integrations & Compliance
              </h2>
              <p style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
                color: 'rgba(255, 255, 255, 0.95)',
                lineHeight: '1.7',
                maxWidth: 'clamp(300px, 80vw, 900px)',
                margin: '0 auto clamp(2rem, 4vw, 3rem)',
                fontWeight: '500'
              }}>
                Connect with your existing systems and maintain regulatory compliance
              </p>
            </div>
          </div>

          <div className="row g-4">
            {/* Integrations */}
            <div className="col-12 col-lg-6">
              <div style={{
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                borderRadius: 'clamp(12px, 3vw, 24px)',
                padding: 'clamp(1.5rem, 4vw, 3rem)',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'clamp(1rem, 2.5vw, 1.5rem)',
                  marginBottom: 'clamp(1.5rem, 4vw, 2.5rem)'
                }}>
                  <div style={{
                    width: 'clamp(60px, 12vw, 80px)',
                    height: 'clamp(60px, 12vw, 80px)',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                    boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                    transition: 'all 0.3s ease'
                  }}>
                    <i className="bi bi-plug"></i>
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: 'clamp(1.2rem, 3vw, 1.6rem)',
                      fontWeight: '700',
                      color: '#ffffff',
                      marginBottom: 'clamp(0.5rem, 1.5vw, 0.75rem)'
                    }}>
                      Platform Integrations
                    </h3>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                      margin: 0,
                      lineHeight: '1.6'
                    }}>
                      Connect with your existing technology stack
                    </p>
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(150px, 30vw, 200px), 1fr))',
                  gap: 'clamp(0.75rem, 2vw, 1rem)'
                }}>
                  {selectedIndustry.integrations.map((integration, index) => (
                    <div key={index} style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(14, 165, 233, 0.2)',
                      borderRadius: 'clamp(12px, 2.5vw, 16px)',
                      padding: 'clamp(1rem, 2.5vw, 1.25rem)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'clamp(0.75rem, 2vw, 1rem)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 30px rgba(14, 165, 233, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                    }}>
                      <i className="bi bi-check-circle" style={{
                        color: '#0ea5e9',
                        fontSize: 'clamp(1.2rem, 2.5vw, 1.4rem)',
                        flexShrink: 0
                      }}></i>
                      <span style={{
                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        fontWeight: '600',
                        color: 'rgba(255, 255, 255, 0.9)'
                      }}>
                        {integration}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Compliance */}
            <div className="col-12 col-lg-6">
              <div style={{
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                borderRadius: 'clamp(12px, 3vw, 24px)',
                padding: 'clamp(1.5rem, 4vw, 3rem)',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 25px 50px rgba(14, 165, 233, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'clamp(1rem, 2.5vw, 1.5rem)',
                  marginBottom: 'clamp(1.5rem, 4vw, 2.5rem)'
                }}>
                  <div style={{
                    width: 'clamp(60px, 12vw, 80px)',
                    height: 'clamp(60px, 12vw, 80px)',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0ea5e9 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                    boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                    transition: 'all 0.3s ease'
                  }}>
                    <i className="bi bi-shield-check"></i>
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: 'clamp(1.2rem, 3vw, 1.6rem)',
                      fontWeight: '700',
                      color: '#ffffff',
                      marginBottom: 'clamp(0.5rem, 1.5vw, 0.75rem)'
                    }}>
                      Compliance & Security
                    </h3>
                    <p style={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                      margin: 0,
                      lineHeight: '1.6'
                    }}>
                      Meet industry standards and regulatory requirements
                    </p>
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(clamp(150px, 30vw, 200px), 1fr))',
                  gap: 'clamp(0.75rem, 2vw, 1rem)'
                }}>
                  {selectedIndustry.compliance.map((item, index) => (
                    <div key={index} style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(14, 165, 233, 0.2)',
                      borderRadius: 'clamp(12px, 2.5vw, 16px)',
                      padding: 'clamp(1rem, 2.5vw, 1.25rem)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'clamp(0.75rem, 2vw, 1rem)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 30px rgba(14, 165, 233, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                    }}>
                      <i className="bi bi-shield-check" style={{
                        color: '#0ea5e9',
                        fontSize: 'clamp(1.2rem, 2.5vw, 1.4rem)',
                        flexShrink: 0
                      }}></i>
                      <span style={{
                        fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                        fontWeight: '600',
                        color: 'rgba(255, 255, 255, 0.9)'
                      }}>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Trends & Insights Section */}
      <section id="trends" className="trends-section py-5" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: 'clamp(3rem, 8vw, 6rem) 0'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(14, 165, 233, 0.06) 0%, transparent 50%)
          `,
          zIndex: 1
        }}></div>

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '900',
                color: '#1e293b',
                marginBottom: '1rem',
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Industry Trends & Insights
              </h2>
              <p style={{
                fontSize: '1.2rem',
                color: '#64748b',
                lineHeight: '1.6',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                Stay ahead with the latest trends and insights in {selectedIndustry.title.toLowerCase()}
              </p>
            </div>
          </div>

          <div className="row g-4">
            {/* Market Trends */}
            <div className="col-12 col-lg-6">
              <div style={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                borderRadius: 'clamp(12px, 3vw, 24px)',
                padding: 'clamp(1.5rem, 4vw, 3rem)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
                height: '100%'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1.5rem'
                  }}>
                    <i className="bi bi-graph-up-arrow"></i>
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: '#1e293b',
                      marginBottom: '0.5rem'
                    }}>
                      Market Trends
                    </h3>
                    <p style={{
                      color: '#64748b',
                      fontSize: '0.9rem',
                      margin: 0
                    }}>
                      Key developments shaping the industry
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  {selectedIndustry.trends?.map((trend, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      padding: '1rem',
                      background: 'rgba(14, 165, 233, 0.05)',
                      borderRadius: '12px',
                      border: '1px solid rgba(14, 165, 233, 0.1)'
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        background: '#0ea5e9',
                        borderRadius: '50%',
                        marginTop: '0.5rem',
                        flexShrink: 0
                      }}></div>
                      <p style={{
                        color: '#374151',
                        fontSize: '0.95rem',
                        lineHeight: '1.5',
                        margin: 0
                      }}>
                        {trend}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Industry Insights */}
            <div className="col-12 col-lg-6">
              <div style={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                borderRadius: 'clamp(12px, 3vw, 24px)',
                padding: 'clamp(1.5rem, 4vw, 3rem)',
                border: '1px solid rgba(14, 165, 233, 0.2)',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)',
                height: '100%'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1.5rem'
                  }}>
                    <i className="bi bi-lightbulb"></i>
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: '#1e293b',
                      marginBottom: '0.5rem'
                    }}>
                      Key Insights
                    </h3>
                    <p style={{
                      color: '#64748b',
                      fontSize: '0.9rem',
                      margin: 0
                    }}>
                      Strategic insights for {selectedIndustry.title.toLowerCase()} leaders
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(14, 165, 233, 0.05) 100%)',
                    borderRadius: '16px',
                    border: '1px solid rgba(14, 165, 233, 0.2)'
                  }}>
                    <h4 style={{
                      color: '#1e293b',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      marginBottom: '0.75rem'
                    }}>
                      AI Adoption Rate
                    </h4>
                    <p style={{
                      color: '#64748b',
                      fontSize: '0.9rem',
                      lineHeight: '1.5',
                      marginBottom: '1rem'
                    }}>
                      {selectedIndustry.title} organizations are rapidly adopting AI solutions to stay competitive.
                    </p>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <div style={{
                        width: '100%',
                        height: '8px',
                        background: 'rgba(14, 165, 233, 0.2)',
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: '75%',
                          height: '100%',
                          background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                          borderRadius: '4px'
                        }}></div>
                      </div>
                      <span style={{
                        color: '#0ea5e9',
                        fontSize: '0.9rem',
                        fontWeight: '700'
                      }}>75%</span>
                    </div>
                  </div>

                  <div style={{
                    padding: '1.5rem',
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                    borderRadius: '16px',
                    border: '1px solid rgba(16, 185, 129, 0.2)'
                  }}>
                    <h4 style={{
                      color: '#1e293b',
                      fontSize: '1.1rem',
                      fontWeight: '700',
                      marginBottom: '0.75rem'
                    }}>
                      ROI Timeline
                    </h4>
                    <p style={{
                      color: '#64748b',
                      fontSize: '0.9rem',
                      lineHeight: '1.5',
                      marginBottom: '1rem'
                    }}>
                      Most organizations see positive ROI within 6-12 months of AI implementation.
                    </p>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <i className="bi bi-clock" style={{ color: '#10b981', fontSize: '1rem' }}></i>
                      <span style={{
                        color: '#10b981',
                        fontSize: '0.9rem',
                        fontWeight: '700'
                      }}>6-12 months</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Industry Comparison Tool */}
      <section id="comparison" className="comparison-section py-5" style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: '6rem 0'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 15% 85%, rgba(14, 165, 233, 0.12) 0%, transparent 60%),
            radial-gradient(circle at 85% 15%, rgba(14, 165, 233, 0.08) 0%, transparent 60%)
          `,
          zIndex: 1
        }}></div>

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '900',
                color: '#ffffff',
                marginBottom: '1rem',
                textShadow: '0 6px 20px rgba(0, 0, 0, 0.4)'
              }}>
                Compare Industries
              </h2>
              <p style={{
                fontSize: '1.2rem',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: '1.6',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                See how our AI solutions adapt across different industries
              </p>
            </div>
          </div>

          <div className="row g-4">
            {getAllIndustries().slice(0, 4).map((industry, index) => (
              <div key={industry.id} className="col-md-6 col-lg-3">
                <div style={{
                  background: 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '20px',
                  padding: '2rem',
                  border: '1px solid rgba(14, 165, 233, 0.2)',
                  textAlign: 'center',
                  transition: 'all 0.4s ease',
                  cursor: 'pointer',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.4)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.2)';
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                }}
                onClick={() => navigate(`/industries/${industry.id}`)}>
                  <div style={{
                    fontSize: '3rem',
                    marginBottom: '1rem'
                  }}>
                    {industry.icon}
                  </div>
                  
                  <h4 style={{
                    color: '#ffffff',
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    marginBottom: '0.75rem'
                  }}>
                    {industry.title}
                  </h4>
                  
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.9rem',
                    lineHeight: '1.5',
                    marginBottom: '1.5rem'
                  }}>
                    {industry.subtitle}
                  </p>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    marginBottom: '1.5rem'
                  }}>
                    {industry.stats.slice(0, 2).map((stat, statIndex) => (
                      <div key={statIndex} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.5rem',
                        background: 'rgba(14, 165, 233, 0.1)',
                        borderRadius: '8px'
                      }}>
                        <span style={{
                          color: 'rgba(255, 255, 255, 0.9)',
                          fontSize: '0.8rem'
                        }}>
                          {stat.label}
                        </span>
                        <span style={{
                          color: '#0ea5e9',
                          fontSize: '0.9rem',
                          fontWeight: '700'
                        }}>
                          {stat.number}
                        </span>
                      </div>
                    ))}
                  </div>

                  <button 
                    style={{
                      width: '100%',
                      background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                      border: 'none',
                      color: '#ffffff',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '12px',
                      fontWeight: '700',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      marginTop: 'auto'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 12px 35px rgba(14, 165, 233, 0.4)';
                      e.target.style.background = 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 8px 25px rgba(14, 165, 233, 0.3)';
                      e.target.style.background = 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)';
                    }}
                    onClick={() => navigate(`/industries/${industry.id}`)}
                  >
                    <span>Explore</span>
                    <i className="bi bi-arrow-right" style={{ fontSize: '0.8rem' }}></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

           <div className="text-center mt-5">
             <Link to="/industries/comparison" style={{
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              border: 'none',
              color: '#ffffff',
              padding: '1rem 2rem',
              borderRadius: '12px',
              fontWeight: '700',
              fontSize: '1rem',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              transition: 'all 0.3s ease',
              boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 12px 35px rgba(14, 165, 233, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 25px rgba(14, 165, 233, 0.3)';
            }}>
              <i className="bi bi-grid-3x3-gap"></i>
              <span>View All Industries</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Project Showcase Section */}
      {getIndustryProjects(industryId).length > 0 && (
        <section id="projects" className="py-5" style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          position: 'relative',
          overflow: 'hidden',
          padding: '6rem 0'
        }}>
          {/* Enhanced Background Effects */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 80%, rgba(14, 165, 233, 0.08) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(14, 165, 233, 0.06) 0%, transparent 50%)
            `,
            zIndex: 1
          }}></div>
          
          <div className="container" style={{ position: 'relative', zIndex: 2 }}>
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '24px',
                  padding: '4rem',
                  border: '1px solid rgba(14, 165, 233, 0.2)',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)'
                }}>
                  <div className="text-center mb-5">
                    <h3 style={{
                      fontSize: 'clamp(2rem, 4vw, 3rem)',
                      fontWeight: '900',
                      color: '#1e293b',
                      lineHeight: '1.1',
                      marginBottom: '2rem'
                    }}>
                      Our Success Stories
                    </h3>
                    <p style={{
                      fontSize: '1.3rem',
                      color: '#64748b',
                      lineHeight: '1.7',
                      maxWidth: '900px',
                      margin: '0 auto 3rem',
                      fontWeight: '500'
                    }}>
                      See how we've delivered real value to {selectedIndustry?.title.toLowerCase()} companies
                    </p>
                  </div>

                  <div className="row g-4">
                    {getIndustryProjects(industryId).map((project, index) => (
                      <div key={project.id} className="col-12 col-lg-6">
                        <div className="project-card" style={{
                          background: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(20px)',
                          borderRadius: 'clamp(12px, 3vw, 24px)',
                          padding: 'clamp(1.5rem, 4vw, 2.5rem)',
                          border: '1px solid rgba(14, 165, 233, 0.15)',
                          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          height: '100%',
                          minHeight: 'clamp(450px, 65vw, 700px)',
                          position: 'relative',
                          overflow: 'hidden',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-12px)';
                          e.currentTarget.style.boxShadow = '0 30px 60px rgba(14, 165, 233, 0.2)';
                          e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 0, 0, 0.08)';
                          e.currentTarget.style.borderColor = 'rgba(14, 165, 233, 0.15)';
                        }}>
                          {/* Mobile-Responsive Grid Layout */}
                          <div className="project-grid" style={{
                            display: 'grid',
                            gridTemplateRows: 'clamp(60px, 10vw, 80px) clamp(70px, 12vw, 90px) clamp(90px, 15vw, 110px) clamp(140px, 25vw, 180px) 1fr clamp(50px, 8vw, 60px)',
                            gap: 'clamp(1rem, 2vw, 1.5rem)',
                            height: '100%',
                            minHeight: 'clamp(400px, 55vw, 650px)'
                          }}>
                            {/* Row 1: Project Icon - Mobile Responsive */}
                            <div style={{
                              minHeight: 'clamp(50px, 10vw, 70px)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <div className="project-icon" style={{
                                width: 'clamp(50px, 10vw, 70px)',
                                height: 'clamp(50px, 10vw, 70px)',
                                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
                                boxShadow: '0 8px 25px rgba(14, 165, 233, 0.3)',
                                transition: 'all 0.3s ease'
                              }}>
                                <i className={`bi ${industryBranding[industryId]?.icon}`} style={{ color: 'white' }}></i>
                              </div>
                            </div>

                            {/* Row 2: Project Title - Mobile Responsive */}
                            <div style={{
                              minHeight: 'clamp(60px, 12vw, 70px)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              padding: '0 clamp(0.5rem, 2vw, 1rem)'
                            }}>
                              <h4 className="project-title" style={{
                                fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
                                fontWeight: '700',
                                color: '#1e293b',
                                lineHeight: '1.4',
                                textAlign: 'center',
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 'clamp(50px, 10vw, 60px)',
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                                hyphens: 'auto'
                              }}>
                                {project.title}
                              </h4>
                            </div>

                            {/* Row 3: Tech Stack - Mobile Responsive */}
                            <div style={{
                              minHeight: 'clamp(80px, 15vw, 90px)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <div className="tech-stack-container" style={{ 
                                display: 'grid',
                                gap: 'clamp(0.3rem, 1vw, 0.5rem)',
                                width: '100%',
                                maxWidth: 'clamp(280px, 80vw, 350px)'
                              }}>
                                {project.tech_stack.slice(0, 4).map((tech, techIndex) => (
                                  <span key={techIndex} className="tech-stack-tag" style={{
                                    background: 'rgba(14, 165, 233, 0.1)',
                                    color: '#0ea5e9',
                                    padding: 'clamp(0.3rem, 1vw, 0.4rem) clamp(0.6rem, 2vw, 0.8rem)',
                                    borderRadius: 'clamp(12px, 2vw, 16px)',
                                    fontSize: 'clamp(0.7rem, 1.5vw, 0.85rem)',
                                    fontWeight: '600',
                                    border: '1px solid rgba(14, 165, 233, 0.2)',
                                    whiteSpace: 'normal',
                                    textAlign: 'center',
                                    minHeight: 'clamp(24px, 5vw, 32px)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'visible',
                                    wordWrap: 'break-word',
                                    lineHeight: '1.2'
                                  }}>
                                    {tech}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Row 4: Impact Metrics - Mobile Responsive with 2 metrics on mobile, 3 on desktop */}
                            <div style={{
                              minHeight: 'clamp(140px, 25vw, 180px)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <div style={{
                                background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.08) 0%, rgba(14, 165, 233, 0.05) 100%)',
                                borderRadius: 'clamp(12px, 2vw, 20px)',
                                padding: 'clamp(1rem, 3vw, 1.5rem)',
                                border: '1px solid rgba(14, 165, 233, 0.1)',
                                width: '100%',
                                minHeight: 'clamp(120px, 20vw, 160px)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                overflow: 'hidden'
                              }}>
                                <h6 style={{
                                  color: '#64748b',
                                  fontSize: 'clamp(0.8rem, 1.8vw, 0.95rem)',
                                  fontWeight: '600',
                                  textAlign: 'center',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  margin: 0,
                                  minHeight: 'clamp(16px, 3vw, 20px)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  Impact Delivered
                                </h6>
                                
                                {/* Mobile: 2 metrics, Desktop: 3 metrics */}
                                <div className="project-impact-metrics" style={{ 
                                  display: 'grid',
                                  gap: 'clamp(0.5rem, 2vw, 1rem)',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  flex: 1,
                                  width: '100%'
                                }}>
                                  {project.impact.slice(0, 2).map((impact, impactIndex) => (
                                    <div key={impactIndex} className="impact-metric" style={{ 
                                      textAlign: 'center',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      minHeight: 'clamp(80px, 15vw, 100px)'
                                    }}>
                                      <div className="metric-value" style={{
                                        fontSize: 'clamp(1.2rem, 3vw, 1.8rem)',
                                        fontWeight: '900',
                                        background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        marginBottom: 'clamp(0.2rem, 1vw, 0.4rem)',
                                        lineHeight: '1',
                                        minHeight: 'clamp(24px, 5vw, 40px)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}>
                                        {impact.metric}
                                      </div>
                                      <div className="metric-label" style={{
                                        fontSize: 'clamp(0.65rem, 1.5vw, 0.8rem)',
                                        fontWeight: '600',
                                        color: '#64748b',
                                        lineHeight: '1.2',
                                        textAlign: 'center',
                                        minHeight: 'clamp(20px, 4vw, 30px)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        wordWrap: 'break-word',
                                        hyphens: 'auto',
                                        whiteSpace: 'normal',
                                        padding: '0 clamp(0.1rem, 1vw, 0.25rem)',
                                        overflow: 'visible'
                                      }}>
                                        {impact.label}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Row 5: Flexible spacer - Takes remaining space */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center'
                            }}></div>

                            {/* Row 6: CTA Button - Mobile Responsive */}
                            <div style={{
                              minHeight: 'clamp(50px, 8vw, 60px)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              paddingTop: 'clamp(0.5rem, 1vw, 0.75rem)'
                            }}>
                              <Link
                                to="/case-studies"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 'clamp(0.3rem, 1vw, 0.5rem)',
                                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                                  border: 'none',
                                  color: '#ffffff',
                                  padding: 'clamp(0.6rem, 2vw, 0.8rem) clamp(1rem, 3vw, 1.5rem)',
                                  borderRadius: 'clamp(8px, 2vw, 12px)',
                                  fontWeight: '700',
                                  fontSize: 'clamp(0.75rem, 1.8vw, 0.9rem)',
                                  textDecoration: 'none',
                                  transition: 'all 0.3s ease',
                                  cursor: 'pointer',
                                  boxShadow: '0 6px 20px rgba(14, 165, 233, 0.3)',
                                  width: '100%',
                                  maxWidth: 'clamp(180px, 40vw, 220px)',
                                  position: 'relative',
                                  overflow: 'hidden',
                                  minHeight: 'clamp(36px, 7vw, 45px)',
                                  whiteSpace: 'nowrap'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 12px 35px rgba(14, 165, 233, 0.4)';
                                  e.target.style.background = 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = '0 8px 25px rgba(14, 165, 233, 0.3)';
                                  e.target.style.background = 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)';
                                }}
                              >
                                <i className="bi bi-file-text" style={{ fontSize: '1rem' }}></i>
                                <span>View Case Study</span>
                                <i className="bi bi-arrow-right" style={{ fontSize: '0.9rem' }}></i>
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}


      {/* Our Commitment Section */}
      <div id="commitment">
      <OurCommitment />
    </div>


      {/* CTA Section */}
      <div id="cta">
        <CTA {...generateCTAMessage('industry', selectedIndustry)} />
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
      `}</style>
    </>
  );
};

export default IndustriesPage;